import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Search, Upload, X, AlertCircle, CheckCircle2, FileSpreadsheet, ChevronDown, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import Modal from '../components/layout/Modal';

const EMPTY = { code: '', name: '', description: '', credits: 3, department: '' };
const REQUIRED_COLS = ['code', 'name'];
const ALL_COLS = ['code', 'name', 'department', 'credits', 'description'];

const inputCls =
  'w-full px-3 py-2.5 bg-white border border-[#E2E0D8] rounded-xl text-[13px] ' +
  'text-[#1a1a18] placeholder-[#B4B2A9] focus:outline-none focus:ring-2 ' +
  'focus:ring-[#1a1a18]/10 focus:border-[#888780] transition-colors';
const labelCls = 'block text-[11px] font-medium text-[#888780] uppercase tracking-wider mb-1.5';

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CoursesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, mode: 'create', data: null });
  const [importOpen, setImportOpen] = useState(false);

  const { data: courses = [], isLoading } = useQuery(
    ['courses'], () => api.get('/courses').then(r => r.data)
  );

  const filtered = courses.filter(c =>
    c.code.toLowerCase().includes(search.toLowerCase()) ||
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const saveMutation = useMutation(
    (form) => modal.mode === 'create'
      ? api.post('/courses', form)
      : api.put(`/courses/${modal.data.id}`, form),
    {
      onSuccess: () => {
        qc.invalidateQueries(['courses']);
        toast.success(modal.mode === 'create' ? 'Course created' : 'Course updated');
        setModal({ open: false, mode: 'create', data: null });
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to save'),
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/courses/${id}`),
    {
      onSuccess: () => { qc.invalidateQueries(['courses']); toast.success('Course deleted'); },
      onError: (err) => toast.error(err.response?.data?.error || 'Cannot delete — may be in use'),
    }
  );

  const bulkMutation = useMutation(
    (rows) => api.post('/courses/bulk', { courses: rows }),
    {
      onSuccess: (res) => {
        qc.invalidateQueries(['courses']);
        toast.success(`${res.data?.created ?? '?'} course(s) imported`);
        setImportOpen(false);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Bulk import failed'),
    }
  );

  return (
    <div className="min-h-screen bg-[#F7F6F2]">

      {/* Header */}
      <header className="bg-[#F7F6F2] border-b border-[#E2E0D8] px-8 py-4
                         flex items-center justify-between sticky top-0 z-10">
        <div>
          <p className="text-[10px] text-[#B4B2A9] uppercase tracking-[0.12em] mb-0.5">
            Timetabling System
          </p>
          <h1 className="text-[18px] font-medium text-[#1a1a18] leading-none">Courses</h1>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E2E0D8]
                       hover:border-[#888780] text-[#5F5E5A] hover:text-[#1a1a18]
                       text-[13px] font-medium rounded-xl transition-colors"
          >
            <Upload className="w-3.5 h-3.5" /> Import
          </button>
          <button
            onClick={() => setModal({ open: true, mode: 'create', data: EMPTY })}
            className="flex items-center gap-2 px-4 py-2 bg-[#1a1a18] hover:bg-[#2C2C2A]
                       text-[#F7F6F2] text-[13px] font-medium rounded-xl transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add course
          </button>
        </div>
      </header>

      <main className="px-8 py-7 max-w-7xl mx-auto">

        {/* Search */}
        <div className="relative mb-5 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#B4B2A9]" />
          <input
            type="text"
            placeholder="Search by code or name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-[#E2E0D8] rounded-xl
                       text-[13px] text-[#1a1a18] placeholder-[#B4B2A9]
                       focus:outline-none focus:border-[#888780] transition-colors"
          />
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E2E0D8] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#F1EFE8]">
                {['Code', 'Name', 'Department', 'Credits', ''].map(h => (
                  <th key={h} className="text-left text-[10px] font-medium text-[#888780]
                                         uppercase tracking-widest px-5 py-3.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="text-center text-[13px] text-[#B4B2A9] py-16">Loading…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="flex flex-col items-center gap-3 py-16">
                      <div className="w-10 h-10 rounded-xl bg-[#F1EFE8] flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-[#B4B2A9]" />
                      </div>
                      <p className="text-[13px] text-[#B4B2A9]">
                        {search ? 'No courses match your search' : 'No courses added yet'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map((c, i) => (
                <tr
                  key={c.id}
                  className={`group hover:bg-[#FAFAF8] transition-colors
                    ${i < filtered.length - 1 ? 'border-b border-[#F1EFE8]' : ''}`}
                >
                  <td className="px-5 py-3.5">
                    <span className="px-2.5 py-1 bg-[#E6F1FB] text-[#185FA5]
                                     text-[11px] font-mono font-medium rounded-lg">
                      {c.code}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-[13px] font-medium text-[#1a1a18]">{c.name}</p>
                    {c.description && (
                      <p className="text-[11px] text-[#B4B2A9] mt-0.5 truncate max-w-xs">
                        {c.description}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-[13px] text-[#5F5E5A]">
                    {c.department || <span className="text-[#D3D1C7]">—</span>}
                  </td>
                  <td className="px-5 py-3.5 text-[13px] text-[#5F5E5A] tabular-nums">
                    {c.credits}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1
                                    opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setModal({ open: true, mode: 'edit', data: c })}
                        className="p-1.5 text-[#888780] hover:text-[#1a1a18]
                                   hover:bg-[#F1EFE8] rounded-lg transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => { if (window.confirm('Delete this course?')) deleteMutation.mutate(c.id); }}
                        className="p-1.5 text-[#888780] hover:text-[#A32D2D]
                                   hover:bg-[#FCEBEB] rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Footer count */}
          {filtered.length > 0 && (
            <div className="border-t border-[#F1EFE8] px-5 py-3">
              <p className="text-[11px] text-[#B4B2A9]">
                {filtered.length} {filtered.length === 1 ? 'course' : 'courses'}
                {search && ` matching "${search}"`}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Edit / Create Modal */}
      <Modal
        isOpen={modal.open}
        onClose={() => setModal(p => ({ ...p, open: false }))}
        title={modal.mode === 'create' ? 'Add course' : 'Edit course'}
      >
        <CourseForm
          initial={modal.data}
          loading={saveMutation.isLoading}
          onSubmit={saveMutation.mutate}
          onCancel={() => setModal(p => ({ ...p, open: false }))}
        />
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import courses"
      >
        <ImportForm
          loading={bulkMutation.isLoading}
          onImport={bulkMutation.mutate}
          onCancel={() => setImportOpen(false)}
        />
      </Modal>
    </div>
  );
}

// ─── Course Form ──────────────────────────────────────────────────────────────

function CourseForm({ initial, loading, onSubmit, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(form); }} className="space-y-4 pt-1">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Course code *</label>
          <input required value={form.code} onChange={set('code')} className={inputCls} placeholder="CS101" />
        </div>
        <div>
          <label className={labelCls}>Credits</label>
          <input type="number" min={1} max={12} value={form.credits} onChange={set('credits')} className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Course name *</label>
        <input required value={form.name} onChange={set('name')} className={inputCls} placeholder="Introduction to Computer Science" />
      </div>
      <div>
        <label className={labelCls}>Department</label>
        <input value={form.department} onChange={set('department')} className={inputCls} placeholder="Computer Science" />
      </div>
      <div>
        <label className={labelCls}>Description</label>
        <textarea rows={3} value={form.description} onChange={set('description')} className={inputCls} placeholder="Optional…" />
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel}
          className="flex-1 py-2.5 bg-white border border-[#E2E0D8] hover:border-[#888780]
                     text-[#5F5E5A] hover:text-[#1a1a18] rounded-xl transition-colors text-[13px] font-medium">
          Cancel
        </button>
        <button type="submit" disabled={loading}
          className="flex-1 py-2.5 bg-[#1a1a18] hover:bg-[#2C2C2A] disabled:opacity-50
                     text-[#F7F6F2] rounded-xl transition-colors text-[13px] font-medium">
          {loading ? 'Saving…' : 'Save course'}
        </button>
      </div>
    </form>
  );
}

// ─── Import Form ──────────────────────────────────────────────────────────────

function ImportForm({ loading, onImport, onCancel }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [errors, setErrors] = useState([]);
  const [fileName, setFileName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const parseFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (raw.length === 0) {
          setErrors([{ row: '-', message: 'File is empty or has no data rows.' }]);
          setRows(null); return;
        }
        const normalized = raw.map(r =>
          Object.fromEntries(Object.entries(r).map(([k, v]) => [k.trim().toLowerCase(), String(v).trim()]))
        );
        const validRows = [], parseErrors = [];
        normalized.forEach((r, i) => {
          const rowNum = i + 2;
          const missing = REQUIRED_COLS.filter(col => !r[col]);
          if (missing.length > 0) { parseErrors.push({ row: rowNum, message: `Missing: ${missing.join(', ')}` }); return; }
          const credits = parseInt(r.credits) || 3;
          if (r.credits && (isNaN(parseInt(r.credits)) || credits < 1 || credits > 12)) {
            parseErrors.push({ row: rowNum, message: `Invalid credits "${r.credits}" — must be 1–12` }); return;
          }
          validRows.push({ code: r.code, name: r.name, department: r.department || '', credits, description: r.description || '' });
        });
        setRows(validRows.length > 0 ? validRows : null);
        setErrors(parseErrors);
      } catch {
        setErrors([{ row: '-', message: 'Could not parse file. Use a valid .xlsx or .csv.' }]);
        setRows(null);
      }
    };
    reader.readAsBinaryString(file);
  }, []);

  const handleReset = () => {
    setRows(null); setErrors([]); setFileName('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ALL_COLS,
      ['CS101', 'Intro to Computer Science', 'Computer Science', 3, 'Foundations of programming.'],
      ['MATH201', 'Calculus I', 'Mathematics', 4, ''],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Courses');
    XLSX.writeFile(wb, 'courses_template.xlsx');
  };

  const hasErrors = errors.length > 0;
  const canImport = rows && rows.length > 0;

  return (
    <div className="space-y-4 pt-1">

      {/* Instructions */}
      <div className="bg-[#F7F6F2] border border-[#E2E0D8] rounded-xl p-4 space-y-2.5">
        <p className="text-[12px] font-medium text-[#1a1a18]">Expected columns</p>
        <div className="flex flex-wrap gap-1.5">
          {ALL_COLS.map(col => (
            <span key={col}
              className="px-2 py-0.5 rounded-lg font-mono text-[11px]"
              style={REQUIRED_COLS.includes(col)
                ? { background: '#E6F1FB', color: '#185FA5' }
                : { background: '#F1EFE8', color: '#888780' }}
            >
              {col}{REQUIRED_COLS.includes(col) ? ' *' : ''}
            </span>
          ))}
        </div>
        <p className="text-[11px] text-[#B4B2A9]">* Required. Headers are case-insensitive.</p>
        <button onClick={downloadTemplate}
          className="text-[11px] text-[#185FA5] hover:underline transition-colors">
          Download template (.xlsx)
        </button>
      </div>

      {/* Drop zone / file chip */}
      {!fileName ? (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); parseFile(e.dataTransfer.files[0]); }}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
            ${dragging ? 'border-[#185FA5] bg-[#E6F1FB]/40' : 'border-[#E2E0D8] hover:border-[#C8C6BE] hover:bg-[#FAFAF8]'}`}
        >
          <FileSpreadsheet className="w-7 h-7 text-[#C8C6BE] mx-auto mb-3" />
          <p className="text-[13px] text-[#1a1a18] font-medium">
            Drop your file here, or <span className="text-[#185FA5]">browse</span>
          </p>
          <p className="text-[11px] text-[#B4B2A9] mt-1">.xlsx or .csv</p>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv"
            onChange={e => parseFile(e.target.files[0])} className="hidden" />
        </div>
      ) : (
        <div className="flex items-center justify-between bg-[#F7F6F2] border border-[#E2E0D8] rounded-xl px-4 py-3">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-4 h-4 text-[#185FA5]" />
            <div>
              <p className="text-[13px] font-medium text-[#1a1a18]">{fileName}</p>
              <p className="text-[11px] text-[#888780]">
                {rows ? `${rows.length} valid row(s)` : '0 valid rows'}
                {hasErrors ? ` · ${errors.length} error(s)` : ''}
              </p>
            </div>
          </div>
          <button onClick={handleReset}
            className="p-1.5 text-[#B4B2A9] hover:text-[#1a1a18] hover:bg-[#E2E0D8] rounded-lg transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Errors */}
      {hasErrors && (
        <div className="bg-[#FCEBEB] border border-[#F09595] rounded-xl p-3 space-y-1.5">
          <p className="text-[11px] font-medium text-[#A32D2D] uppercase tracking-wider flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" /> {errors.length} row(s) skipped
          </p>
          <ul className="space-y-0.5 max-h-28 overflow-y-auto">
            {errors.map((err, i) => (
              <li key={i} className="text-[11px] text-[#791F1F]">Row {err.row}: {err.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Preview */}
      {rows && rows.length > 0 && (
        <div>
          <button onClick={() => setShowPreview(p => !p)}
            className="flex items-center gap-1.5 text-[11px] font-medium text-[#888780]
                       hover:text-[#1a1a18] transition-colors mb-2">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPreview ? '' : '-rotate-90'}`} />
            Preview ({rows.length} rows)
          </button>
          {showPreview && (
            <div className="border border-[#E2E0D8] rounded-xl overflow-hidden max-h-52 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#F7F6F2] border-b border-[#E2E0D8]">
                  <tr>
                    {['Code', 'Name', 'Dept', 'Credits'].map(h => (
                      <th key={h} className="text-left text-[10px] font-medium text-[#888780]
                                             uppercase tracking-wider px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`${i < rows.length - 1 ? 'border-b border-[#F1EFE8]' : ''} hover:bg-[#FAFAF8]`}>
                      <td className="px-3 py-2 font-mono text-[#185FA5]">{r.code}</td>
                      <td className="px-3 py-2 text-[#1a1a18]">{r.name}</td>
                      <td className="px-3 py-2 text-[#888780]">{r.department || '—'}</td>
                      <td className="px-3 py-2 text-[#888780]">{r.credits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Success */}
      {canImport && !hasErrors && (
        <div className="flex items-center gap-2 text-[13px] bg-[#EAF3DE] border border-[#C0DD97]
                        text-[#3B6D11] rounded-xl px-3 py-2.5">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          All {rows.length} rows validated successfully
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onCancel}
          className="flex-1 py-2.5 bg-white border border-[#E2E0D8] hover:border-[#888780]
                     text-[#5F5E5A] hover:text-[#1a1a18] rounded-xl transition-colors text-[13px] font-medium">
          Cancel
        </button>
        <button
          onClick={() => canImport && onImport(rows)}
          disabled={!canImport || loading}
          className="flex-1 py-2.5 bg-[#1a1a18] hover:bg-[#2C2C2A] disabled:opacity-40
                     disabled:cursor-not-allowed text-[#F7F6F2] rounded-xl transition-colors text-[13px] font-medium">
          {loading ? 'Importing…' : `Import ${rows?.length ?? 0} course(s)`}
        </button>
      </div>
    </div>
  );
}